<template lang="pug">
  div
    VeeForm(
      ref="form"
      tag="div"
      :validation-schema="formSchema"
      :class="[$style.formItem]"
      @submit="submitForm")

      div(v-if="hasAvailabilityRemark" class="block lg:hidden m-0 md:mb-16 w-full mt-24 md:mt-8")
        AvailabilityRemark(:block="block")

      div(class="w-full md:w-6/12 order-2 px-16 md:pr-8 mt-24 md:mt-8")
        NewLocationPickupInput(
          :delivery-input="$refs.deliveryInput"
          ref="pickupInput"
          :block="locationBlock"
          :lazy="false"
          @focus="scrollInputToTop('pickup')")
      div(
        v-if="!propertyTypeHidden || !propertyFloorHidden"
        class="w-full md:w-6/12 order-3 md:order-4 px-16 md:pr-8")
        PropertyPickupInput(
          :block="propertyBlock"
          :property-type-hidden="propertyTypeHidden"
          :property-floor-hidden="propertyFloorHidden"
          :lazy="false")

      div(class="w-full md:w-6/12 order-4 md:order-3 px-16 md:pl-8 mt-24 md:mt-8")
        NewLocationDeliveryInput(
          ref="deliveryInput"
          :block="locationBlock"
          :lazy="false"
          @focus="scrollInputToTop('delivery')")

      div(
        v-if="!propertyTypeHidden || !propertyFloorHidden"
        class="w-full md:w-6/12 order-5 px-16 md:pl-8"
      )
        PropertyDeliveryInput(
          :block="propertyBlock"
          :property-type-hidden="propertyTypeHidden"
          :property-floor-hidden="propertyFloorHidden"
          :lazy="false"
        )

      div(class="flex justify-between w-full flex-col-reverse md:flex-row order-6 mt-16 p-16 pt-0")
        div(:class="$style.trustPilotWrapper")
          client-only(v-if="trustpilotCustomers !== '0'")
            div(
              :class="$style.trustpilotHead")
              figure
                img(
                  src="~/assets/img/trustpilot/brand/brandmark_gr-blk.svg"
                  class="fixed-size"
                  width="73"
                  height="18"
                  alt="Trustpilot")
              span {{ formattedTrustpilotCustomers }}
            figure(class="mt-0 md:mt-8")
              img(
                src="~/assets/img/trustpilot/brand/stars-4.5.svg"
                class="fixed-size"
                width="128"
                height="24"
                alt="Trustpilot rating, 4.5 out of 5 stars")
        template(v-if="hasCallUs")
          div(class="call-us-description") {{ $t('location_form.tap_to_call') }}
          PhoneNumber(
            :class="['call-us-container', hasRetrieveQuote ? 'mt-0' : 'mt-16']"
            :showIcon="true"
            :showButtonText="true"
            :handleClick="handleCallUsClick"
            :buttonText="$t('location_form.call_us')")
        div(:class="hasRetrieveQuote ? 'text-center md:flex md:flex-row-reverse md:flex-wrap md:gap-16' : ''")
          div(class="av-column av-column-container av-buttons")
            Btn(
              kind="rebrand-cta"
              @click="submitForm"
              :block="true"
              :wp-block="ctaBlock.innerBlocks[1].innerBlocks[0].innerBlocks[0]"
              size="xl"
              :disabled="submittingForm"
              :loading="submittingForm")
              .text-rebrandSm {{ getInnerText(ctaBlock.innerBlocks[1]?.innerBlocks[0]?.innerBlocks[0]?.innerHtml) }}
          button(
            v-show="hasRetrieveQuote"
            @click.prevent="onEmailFormOpen"
            class="p-16 md:mr-2 md:mb-0 md:p-0 italic underline text-base text-anyvan-cta-secondary cursor-pointer md:whitespace-nowrap"
          ) {{ $t("location_form.already_received_quote") }}
    div(class="hidden lg:block w-full")
      AvailabilityRemark(v-if="hasAvailabilityRemark" :block="block")
    RetrieveQuoteModal(v-if="hasRetrieveQuote && emailFormShow" @close-modal="onEmailFormClose")
</template>

<script lang="ts">
import { defineComponent } from "vue"
import { type ValidationResult } from "vee-validate"
import axios from "axios"
import { mapGetters, mapActions } from "vuex"
import { object, string } from "yup"
import { toTypedSchema } from "@vee-validate/yup"
import { patternCheck } from "~/utils/block"
import { formatNumberWithLocale } from "~/utils/formatNumberWithLocale"
import { GET_LISTING, SET_DELIVERY_PROPERTY_TYPE, SET_PICKUP_PROPERTY_TYPE } from "~/store/listing"
import {
  ListingService,
  getV4Url,
  getV4ListingPayload,
  addPreListingIdToSessionStorage,
  isValidPropertyType
} from "~/services/public/Listing"
import { AVAILABILITY_REMARK, CALL_US, RETRIEVE_QUOTE } from "~/constants/blocks"
import { redirectToV2 } from "~/utils/v4-redirect"
import { GET_PHONE_NUMBER_VALUE } from "~/store/phone-number"
import { extractHref, getInnerText, getv4LocaleFromI18nLocale } from "~/scripts/useful-functions"
import { GET_WINDOW_WIDTH } from "~/store/pages"
import {
  ADD_FLOORS,
  ADD_PROPERTY_TYPES,
  ADD_PROPERTY_TYPES_FLAT,
  ADD_PROPERTY_TYPES_HOUSE
} from "~/store/property"
import { CATEGORY_V4_ID_HOUSE_MOVE } from "~/constants/category"
import { LOCALE } from "~/constants/locale"

interface ComponentData {
  submittedForm: boolean
  submittingForm: boolean
  emailFormShow: boolean
  trustpilotCustomers: string
}

export default defineComponent({
  name: "RemovalsLocationForm",

  props: {
    initialPropertyType: {
      type: String,
      default: ""
    },
    propertyTypeHidden: {
      type: Boolean
    },
    propertyFloorHidden: {
      type: Boolean
    },
    block: {
      type: Object as () => Block,
      required: true
    }
  },

  data(): ComponentData {
    return {
      submittedForm: false,
      submittingForm: false,
      emailFormShow: false,
      trustpilotCustomers: "0"
    }
  },

  computed: {
    ...mapGetters("pages", {
      getWindowWidth: GET_WINDOW_WIDTH
    }),
    ...mapGetters("listing", {
      getListing: GET_LISTING
    }),
    ...mapGetters("phone-number", {
      getPhoneNumberValue: GET_PHONE_NUMBER_VALUE
    }),

    formSchema() {
      const locationFormSchema: Record<string, any> = {
        pickupLocation: string().required(),
        deliveryLocation: string().required()
      }
      if (!this.propertyTypeHidden) {
        locationFormSchema["pickupPropertyType"] = string().required()
        locationFormSchema["deliveryPropertyType"] = string().required()
      }
      return toTypedSchema(object(locationFormSchema))
    },

    locationBlock(): Record<string, any> {
      return this.block.innerBlocks[0]
    },

    propertyBlock(): Record<string, any> {
      return this.block.innerBlocks[this.block.innerBlocks.length - 2]
    },

    ctaBlock(): Record<string, any> {
      return this.block.innerBlocks[this.block.innerBlocks.length - 1]
    },

    hasAvailabilityRemark(): boolean {
      return this.patternCheck(this.block.attributes, AVAILABILITY_REMARK)
    },

    hasCallUs(): boolean {
      return this.patternCheck(this.block.attributes, CALL_US)
    },

    hasRetrieveQuote(): boolean {
      return this.patternCheck(this.block.attributes, RETRIEVE_QUOTE)
    },

    formattedTrustpilotCustomers(): string {
      return formatNumberWithLocale(this.trustpilotCustomers, this.$i18n.locale)
    }
  },

  watch: {
    submittingForm(val: boolean): void {
      if (val) {
        this.$emitter.emit("submitting-location-form")
        return
      }
      this.$emitter.emit("submitted-location-form")
    }
  },

  async created() {
    if (this.initialPropertyType) {
      this.setPickupPropertyType(this.initialPropertyType)
      this.setDeliveryPropertyType(this.initialPropertyType)
    }

    const trustPilotConfig = this.$config.public.trustPilot
    let trustPilotId = trustPilotConfig.id
    let trustPilotAPIKey = trustPilotConfig.apiKey
    if (this.$i18n.locale !== LOCALE.UK) {
      const upperCaseLocale = this.$i18n.locale.toUpperCase()
      trustPilotId = trustPilotConfig[`id${upperCaseLocale}`]
      trustPilotAPIKey = trustPilotConfig[`apiKey${upperCaseLocale}`]
    }
    const trustpilotURL = `https://api.trustpilot.com/v1/business-units/${trustPilotId}`
    try {
      const axiosInstance = axios.create() as any
      const response = await axiosInstance.get(trustpilotURL, {
        params: {
          apikey: trustPilotAPIKey
        }
      })
      if (response.status === 200 && response.data) {
        this.trustpilotCustomers = response.data.numberOfReviews.total
      }
    } catch (err) {
      console.log("Trustpilot error", err)
    }

    const itemsUrl = this.$config.public.v4Host + "/api/v4/booking-flow/config/house-move"
    try {
      const axiosInstance = axios.create() as any
      const headers = {
        headers: {
          "X-Requested-With": "XMLHttpRequest",
          "AV-locale": getv4LocaleFromI18nLocale(this.$i18n.locale)
        }
      }
      const response = await axiosInstance.get(itemsUrl, headers)
      if (response.status === 200 && response.data) {
        this.addPropertyTypes(response.data.propertyTypes)
        this.addFloors(response.data.propertyFloors.floors)
        const houseObject = response.data.propertyTypes.find(item => item.identifier === "house")
        this.addPropertyTypesHouse(houseObject.values)
        const flatObject = response.data.propertyTypes.find(item => item.identifier === "flat")
        this.addPropertyTypesFlat(flatObject.values)
      }
    } catch (err) {
      console.log("Property config error", err)
    }
  },

  mounted() {
    if (this.initialPropertyType) {
      this.setPickupPropertyType(this.initialPropertyType)
      this.setDeliveryPropertyType(this.initialPropertyType)
    }
  },

  beforeMount() {
    this.$emitter.on("submit-location-form", this.submitForm)
  },

  beforeUnmount() {
    this.$emitter.off("submit-location-form")
  },

  methods: {
    ...mapActions("listing", {
      setPickupPropertyType: SET_PICKUP_PROPERTY_TYPE,
      setDeliveryPropertyType: SET_DELIVERY_PROPERTY_TYPE
    }),
    ...mapActions("property", {
      addFloors: ADD_FLOORS,
      addPropertyTypes: ADD_PROPERTY_TYPES,
      addPropertyTypesHouse: ADD_PROPERTY_TYPES_HOUSE,
      addPropertyTypesFlat: ADD_PROPERTY_TYPES_FLAT
    }),

    extractHref,
    getInnerText,
    patternCheck,

    scrollInputToTop(el: string): void {
      if (this.getWindowWidth && this.getWindowWidth > 768) return

      let element
      switch (el) {
        case "pickup":
          element = this.$refs.pickupInput
          break
        case "delivery":
          element = this.$refs.deliveryInput
          break
        default:
          return
      }
      if (element && element.$el.offsetTop) {
        window.scrollTo({
          top: element.$el.offsetTop - 25,
          behavior: "smooth"
        })
      }
    },

    async submitForm(): Promise<void> {
      if (this.submittingForm) return
      this.submittedForm = true
      this.submittingForm = true
      try {
        const listing = this.getListing
        if (!isValidPropertyType(listing?.pickup.propertyType)) {
          await this.setPickupPropertyType(null)
        }
        if (!isValidPropertyType(listing?.delivery.propertyType)) {
          await this.setDeliveryPropertyType(null)
        }
        const validationResult: ValidationResult = await (this.$refs.form as any).validate()

        if (!validationResult.valid) {
          this.submittingForm = false
          this.$emit("close-dropdown")
          return
        }

        const categoryV4Id = CATEGORY_V4_ID_HOUSE_MOVE

        this.$snowplow.trackButtonClick(
          getInnerText(this.ctaBlock.innerBlocks[1].innerBlocks[0].innerBlocks[0].innerHtml),
          this.$config.public.v4Host + "/" + CATEGORY_V4_ID_HOUSE_MOVE
        )

        const payload = getV4ListingPayload(listing, categoryV4Id, this.$i18n.locale)

        // This condition redirects to auction if the locations are international
        // or one of the locations is on an island.
        if (payload.v === 2) {
          redirectToV2(payload, listing, this.$config.public.v4Host)
          this.submittingForm = false
          return
        }

        const listingService = new ListingService(this.$axios, this.$config.public)
        const response: ListingResponse = await listingService.postPreListing(
          payload,
          this.$i18n.locale
        )

        if (response?.data && response.data?.pre_listing_id && response.data?.category_id) {
          if (response.is_international) {
            redirectToV2(payload, listing, this.$config.public.v4Host)
            this.submittingForm = false
            return
          }

          addPreListingIdToSessionStorage(response.data)
          document.addEventListener("visibilitychange", () => {
            this.submittingForm = false
            this.submittedForm = false
          })

          window.location.href = getV4Url(
            this.$config.public.v4Host,
            response.data,
            payload,
            this.$i18n.locale
          )
          return
        } else {
          this.submittingForm = false
        }
      } catch (err) {
        this.submittingForm = false
        console.log("PreListing creation error", err)
      }
    },

    onEmailFormOpen(): void {
      this.emailFormShow = true
    },

    onEmailFormClose(): void {
      this.emailFormShow = false
    },

    handleCallUsClick(label: string = "location_form_button"): void {
      this.$snowplow.trackCallUs({
        phoneNumber: this.getPhoneNumberValue.baseRaw,
        label,
        action: "click",
        extension: this.getPhoneNumberValue.extension
      })
    }
  }
})
</script>

<style lang="scss" module>
.formItem {
  @apply relative z-[2] flex flex-wrap mt-30 bg-white;
  box-shadow:
    0 2px 6px 2px rgba(0, 0, 0, 0.15),
    0 1px 2px 0 rgba(0, 0, 0, 0.3);
  border-radius: 6px;

  :global {
    .av-column-container {
      min-width: max-content;

      .av-button {
        @apply mt-0;
        border-radius: 5px;
      }

      .av-buttons > .av-button {
        @apply mt-0 w-full text-center;
      }
    }

    .call-us-description {
      @apply text-sm text-center italic;
    }
    .call-us-container {
      transition: 0.2s;
      &:hover {
        filter: saturate(1.5) brightness(0.9);
      }
      a {
        @apply flex justify-center w-full h-60 p-10 font-bold text-rebrandSm text-white no-underline bg-anyvan-cta-secondary border border-black/20 lg:text-black70 lg:bg-white;
        border-radius: 8px;
        line-height: 34px;

        svg {
          @apply mt-[-2px];
        }
      }
    }

    .disabled-div {
      & > div:first-child {
        @apply text-gray-600 cursor-not-allowed;
      }
    }
  }

  .trustPilotWrapper {
    @apply flex flex-row md:flex-col items-start justify-between md:justify-center mt-16 md:mt-0;
    /* Keep the space for content while the TrustPilot API is responding   */
    height: 23px;
    min-width: 135px;
  }

  .trustpilotHead {
    display: flex;
    width: 125px;
    min-width: 125px;
    margin-right: 10px;

    div {
      width: 60%;

      figcaption {
        display: none;
      }
    }

    span {
      display: block;
      width: 40%;
      text-align: right;
      font-size: 12px;
      font-weight: 600;
      padding-top: 2px;
      padding-left: 5px;
      line-height: 18px !important;
    }
  }
}

@screen sm {
  .formItem {
    @apply pt-16;
  }
}

@screen md {
  .formItem {
    .trustPilotWrapper {
      height: 62px;
    }

    :global {
      .call-us-description,
      .call-us-container:not(.call-us-cta) {
        @apply hidden;
      }
    }
  }
}

@screen lg {
  .formItem {
    @apply min-w-[487px];
  }
}
</style>
